import type { HelpTopic } from './help/types.js'
import type { ComponentType, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { AdminClient } from './api/adminClient.js'
import type { SessionRole } from './api/client.js'
import type { NavSection } from './ui/icons.js'

/**
 * What a module may contribute to the shells (web modules plan phase 4):
 * the web's counterpart of the API's `AppModule`. A module declares its
 * contributions in `modules/<name>/webModule.tsx`, the root lists every
 * module in `src/webModules.ts`, and the shells render from that list, so
 * adding a domain is a folder and a line there. An add-on (`src/features/`)
 * declares the same kinds of thing through `FeatureWebModule`, whose
 * contributions the shells gate on the client's license and the facility's
 * switch.
 */

/** `sales` is a login for building and showing demos: it reaches the demo clients it created and nothing else in the admin area. */
export type AdminRole = 'root' | 'admin' | 'sales'

/** What the admin shell knows about this sign-in (`POST /admin/login`), plus what has been looked at since. */
export interface AdminSession {
  username: string
  role: AdminRole
  firstName: string
  lastName: string
  loggedInAt: string
  previousLoginAt: string | null
  newFeedback: number
  /** Feedback that arrived since the previous sign-in and has not been opened yet; cleared by opening the Feedback page. */
  unseenFeedback: number
}

/** What every admin page is given. `Api` is the admin calls the page's module needs; the shell's full `AdminApi` satisfies any of them. */
export interface AdminPageProps<Api> {
  api: Api
  session: AdminSession
  /** Told when any admin's password was rotated; the shell switches its own credential when it was the signed-in admin's. */
  onPasswordRotated: (username: string, password: string) => void
  /** Told when any admin's name was set; the header follows when it was the signed-in admin's. */
  onNameChanged: (admin: { username: string; firstName: string; lastName: string }) => void
  /** Told that the new feedback has been looked at, which clears the badge. */
  onFeedbackSeen: () => void
}

/** The admin menu's groups, each a tab in the header's first row; the shell owns their order, headings and icons. `demos` is the sales login's one group. */
export type AdminMenuGroup = 'accounts' | 'directory' | 'scoring' | 'integration' | 'product' | 'system' | 'demos'

/** One page of the admin area, as a module declares it. */
export interface AdminPageContribution<Api> {
  path: string
  /** Short name in the menu. */
  label: string
  /** Heading shown above the page. */
  title: string
  icon: LucideIcon
  group: AdminMenuGroup
  page: (props: AdminPageProps<Api>) => ReactNode
  /** Shown to, and reachable by, the root admin only. */
  rootOnly?: boolean
  /** A page of a sales login's area rather than an admin's; the two menus never mix. */
  sales?: boolean
  /** Reached from another page, not the menu: that page's menu item stays lit, and a link back to it shows above the title. */
  reachedFrom?: { path: string; label: string }
  /** A count to flag on the menu item, from the session. */
  badge?: (session: AdminSession) => number
  /** Called whenever the page becomes the one on screen. */
  onShown?: (props: Pick<AdminPageProps<Api>, 'session' | 'onFeedbackSeen'>) => void
}

/** A tab in the operator shell's facility navigation. */
export interface OperatorTabContribution {
  /** The URL segment under a facility (`/facilities/{id}/<segment>`, or `/<segment>` for a single-facility client) and the tab's label. */
  segment: string
  label: string
  /** The page, given the facility it is showing. */
  page: ComponentType<{ facilityId: string }>
}

/**
 * A page of the operator shell that belongs to the client rather than to a facility (the Executive Insights portfolio):
 * reached at `/<segment>` whatever facility is open, listed in the bar after Reporting and on the facilities list's bar,
 * hidden until the client is licensed. The page gates itself, since no facility switch applies.
 */
export interface OperatorPageContribution {
  segment: string
  label: string
  icon: NavSection
  page: ComponentType<{ role: SessionRole }>
}

/** What a roster contribution is told: the facility, who is looking, and where the facility's Add-ons tab is for the gate's advice. */
export interface RosterContext {
  facilityId: string
  role: SessionRole
  addOnsHref: string
}

/** A tab of an open roster row, beside Scoring. */
export interface RosterRowTabContribution {
  key: string
  label: string
  panel: ComponentType<RosterContext & { workerId: string }>
  /** A count the tab wears, read as a hook so it can be a query. */
  useBadge?: (workerId: string) => number
}

export interface WebModule<Api = unknown> {
  /** The module's name, as its folder under src/modules. `Api` is the admin calls its pages need; a module with no admin page leaves it unknown. */
  name: string
  adminPages?: readonly AdminPageContribution<Api>[]
  operatorTabs?: readonly OperatorTabContribution[]
  rosterRowTabs?: readonly RosterRowTabContribution[]
  /** The help topics for the module's screens (docs/plans/in-app-help-plan.md), opened by `HelpIcon`. */
  helpTopics?: readonly HelpTopic[]
}

/** A setting the platform admin may set for a client on the add-on's license (`settings`), as the license form renders it. */
export interface LicenseSettingField {
  key: string
  label: string
  kind: 'number' | 'boolean'
  /** The deployment's default, in words, shown as the placeholder. */
  defaultHint: string
}

/**
 * An add-on's contributions (docs/plans/feature-licensing-plan.md section
 * 9): an add-on lives in `src/features/<key>/` and declares here what it
 * contributes to the shells. Each contribution renders inside `FeatureGate`
 * or gates itself, so a shell never has to know an add-on's rules.
 */
export interface FeatureWebModule {
  key: string
  name: string
  /** An icon button in the operator shell's header (the AI briefing's), given the facility open - null on the facilities list or a client page - whether a client page (an add-on's own) is open, and who is looking; the add-on gates itself. */
  headerAction?: ComponentType<{ facilityId: string | null; onClientPage: boolean; role: SessionRole }>
  /** Actions another page renders in its own toolbar, by that page's name (`insights`): the AI summary button on the Insights page. */
  pageActions?: readonly { page: string; action: ComponentType<{ role: SessionRole }> }[]
  /** Pages of the admin area, as a module declares its own; the add-on builds its calls on the client the shell's api carries. */
  adminPages?: readonly AdminPageContribution<{ client: AdminClient }>[]
  /** The settings the platform admin may set for a client on the add-on's license, rendered by the license form. */
  licenseSettings?: readonly LicenseSettingField[]
  /** A tab in the operator shell's facility navigation, hidden when the client is unlicensed and marked Off when the facility is not switched on. */
  operatorTab?: OperatorTabContribution
  /** A page of the client's rather than a facility's, hidden when the client is unlicensed; the page gates itself. */
  operatorPage?: OperatorPageContribution
  /** A tab of an open roster row beside Scoring, listed while the client is licensed and marked Off where the facility is not switched on; the add-on gates the body itself. */
  workerTab?: { label: string; panel: ComponentType<RosterContext & { workerId: string }> }
  /** A section of the facility's Reporting tab; the add-on gates it itself. */
  reportingSection?: ComponentType<RosterContext>
  /** A tab of the worker's own app, after the core tabs; the shell lists it only while the add-on is on at the worker's facility (`worker.features` on `/auth/me`). */
  workerAppTab?: { label: string; page: ComponentType<{ workerId: string }> }
  /** A read-only section of the admin's worker detail. The add-on builds its own admin calls on the client, as it mounts its own routes on the API. */
  adminWorkerSection?: ComponentType<{ client: AdminClient; clientId: string; workerId: string }>
  /** A read-only section of the admin's facility dialog. */
  adminFacilitySection?: ComponentType<{ client: AdminClient; clientId: string; facilityId: string }>
  /** A read-only line or section of the admin's client detail, under its own heading. */
  adminClientSection?: { heading: string; section: ComponentType<{ client: AdminClient; clientId: string }> }
  /** The help topics for the add-on's screens, as a module declares its own. */
  helpTopics?: readonly HelpTopic[]
}

export function featureWithSegment(features: readonly FeatureWebModule[], segment: string): FeatureWebModule | null {
  return features.find((feature) => feature.operatorTab?.segment === segment) ?? null
}

/** The add-on whose client-level page a URL's first segment names, if any. */
export function featureWithPage(features: readonly FeatureWebModule[], segment: string | undefined): FeatureWebModule | null {
  return segment === undefined ? null : (features.find((feature) => feature.operatorPage?.segment === segment) ?? null)
}
